/**
 * GuidedSurveyProgress
 *
 * P_GUIDED_SURVEY_EXPERIENCES_1.0 — Honest progress UX
 *
 * Strict contract (locked, non-negotiable):
 *   - No fake progress. No fixed-duration animation.
 *   - The bar reflects the REAL backend work. We poll
 *     GET /api/surveys/guided/status/{id} until `ready=true`.
 *   - We NEVER reach 100% before the backend confirms the survey is
 *     genuinely usable. It is acceptable to pause at 82 / 91 / 96 / 99%
 *     for as long as the work continues.
 *   - Elapsed time is visible to reassure the user.
 *   - Contextual hints surface when steps are taking longer than usual.
 *   - The completion card is shown ONLY after `ready=true`.
 *
 * Backend integration:
 *   - POST /api/surveys/guided/instantiate → returns created survey
 *     immediately (synchronous part). Document is persisted; the
 *     background translation pipeline is what takes real time.
 *   - GET /api/surveys/guided/status/{id} returns
 *     {ready, found, stage, progress_pct, status?, translation_error?}.
 *     stage values: queued | translating | translating_fr |
 *     translating_es | done | failed (and historical: retry_scheduled).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    ArrowRight, Check, Loader2, AlertTriangle, Sparkles, RotateCcw, ChevronLeft, Clock,
} from 'lucide-react';

import { Button } from '../../../components/ui/button';
import { Progress } from '../../../components/ui/progress';
import { surveysAPI } from '../api';

// ─── Visible step model ─────────────────────────────────────────
// The five visible steps map to ranges on the progress bar. Each step
// has a *ceiling* — the maximum percentage we reach while that step is
// "active". We never overshoot a step until the backend has produced
// the signal that step represents.
const STEPS = [
    { id: 'selecting',   key: 'selecting',   defaultLabel: 'Selecting survey template',                ceiling:  8 },
    { id: 'building',    key: 'building',    defaultLabel: 'Building questions',                       ceiling: 16 },
    { id: 'flow',        key: 'flow',        defaultLabel: 'Creating survey flow',                     ceiling: 24 },
    { id: 'translating', key: 'translating', defaultLabel: 'Translating to English, French & Spanish', ceiling: 88 },
    { id: 'saving',      key: 'saving',      defaultLabel: 'Saving survey',                            ceiling: 95 },
    { id: 'preparing',   key: 'preparing',   defaultLabel: 'Preparing editor',                         ceiling: 99 },
];

const POLL_INTERVAL_MS = 1500;
// While the API call hasn't resolved yet we don't know the survey_id,
// so we can't poll. We crawl the bar very gently (capped at the
// "flow" step ceiling) until we have a survey_id, then we hand over
// to the polling-driven progress.
const PRE_API_CRAWL_RATE_PCT_PER_SEC = 2.5;

// Stage hints — friendly business-language messages that explain a
// long-running step. Resolved through i18next so EN/FR/ES stay in sync.
function stageHintKey(stage, elapsedMs) {
    if (!stage) return null;
    if (stage === 'queued') return elapsedMs > 6000 ? 'queueLonger' : null;
    if (stage === 'translating' || stage === 'translating_fr' || stage === 'translating_es') {
        return elapsedMs > 12000 ? 'translatingLonger' : 'translating';
    }
    if (stage === 'failed') return 'translationFailed';
    return null;
}

// Map a backend translation_meta.progress_pct (0..100) into our
// visible bar's translation range [STEP[2].ceiling .. STEP[3].ceiling].
function mapTranslationPct(backendPct) {
    const from = STEPS[2].ceiling;     // 24
    const to = STEPS[3].ceiling;       // 88
    const safe = Math.max(0, Math.min(100, backendPct || 0));
    return from + (safe / 100) * (to - from);
}

const GuidedSurveyProgress = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // ── Read wizard payload from router state ────────────────────
    const payload = location.state?.payload || null;
    const outcomeMeta = location.state?.outcomeMeta || null;

    // If someone deep-links here without a payload, bounce them back.
    useEffect(() => {
        if (!payload) navigate('/surveys', { replace: true });
    }, [payload, navigate]);

    // ── State ────────────────────────────────────────────────────
    const [progress, setProgress] = useState(0);
    const [stepIdx, setStepIdx] = useState(0);
    const [stage, setStage] = useState('queued');
    const [elapsedMs, setElapsedMs] = useState(0);
    const [createdSurvey, setCreatedSurvey] = useState(null);
    const [ready, setReady] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [translationWarning, setTranslationWarning] = useState(null);

    // Refs — keep across renders without re-running the polling effect.
    const startedRef = useRef(false);
    const startTsRef = useRef(0);
    const surveyIdRef = useRef(null);
    const apiResolvedRef = useRef(false);
    const readyRef = useRef(false);
    // The latest progress *target* the bar is allowed to reach. The
    // visible `progress` value catches up to this target via an
    // animation loop — that way the bar always moves smoothly even
    // when poll responses arrive every 1.5 seconds.
    const targetPctRef = useRef(0);

    // ── Elapsed-time ticker (always running while building) ──────
    useEffect(() => {
        if (!payload || ready || errorMsg) return;
        startTsRef.current = performance.now();
        const t1 = setInterval(() => {
            setElapsedMs(performance.now() - startTsRef.current);
        }, 250);
        return () => clearInterval(t1);
    }, [payload, ready, errorMsg]);

    // ── Step derivation from current progress + stage ────────────
    useEffect(() => {
        // Move the step indicator based on progress crossing each
        // step's ceiling AND the stage reported by the backend.
        let idx = 0;
        for (let i = STEPS.length - 1; i >= 0; i--) {
            if (progress >= STEPS[i].ceiling - 2) { idx = i; break; }
        }
        // Force-active 'translating' the moment the backend reports it,
        // even if the bar visually hasn't crossed STEP[3].ceiling.
        if ((stage === 'translating' || stage === 'translating_fr' || stage === 'translating_es') && idx < 3) {
            idx = 3;
        }
        if (idx > stepIdx) setStepIdx(idx);
    }, [progress, stage, stepIdx]);

    // ── Smoothing animator: catch the visible bar up to its target ──
    useEffect(() => {
        if (!payload || errorMsg) return;
        let cancelled = false;
        let rafId;
        const tick = () => {
            if (cancelled) return;
            setProgress((cur) => {
                const target = targetPctRef.current;
                if (cur >= target) return cur;
                // Ease toward target — move ~12% of the remaining gap per frame,
                // but always at least 0.08% so we never visually stall.
                const delta = Math.max(0.08, (target - cur) * 0.12);
                return Math.min(target, cur + delta);
            });
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);
        return () => {
            cancelled = true;
            if (rafId) cancelAnimationFrame(rafId);
        };
    }, [payload, errorMsg]);

    // ── Pre-API crawl: gently move toward the "flow" ceiling ─────
    useEffect(() => {
        if (!payload || errorMsg) return;
        if (apiResolvedRef.current) return;
        const cap = STEPS[2].ceiling - 2;  // crawl up to ~22%
        const startedAt = performance.now();
        let cancelled = false;
        const t1 = setInterval(() => {
            if (cancelled) return;
            if (apiResolvedRef.current) { clearInterval(t1); return; }
            const seconds = (performance.now() - startedAt) / 1000;
            // Linear crawl with a hard cap. Plenty of room for the API to
            // catch up before we ever bump into the ceiling.
            const next = Math.min(cap, seconds * PRE_API_CRAWL_RATE_PCT_PER_SEC);
            if (next > targetPctRef.current) targetPctRef.current = next;
        }, 120);
        return () => { cancelled = true; clearInterval(t1); };
    }, [payload, errorMsg]);

    // ── Kick off the actual API call ─────────────────────────────
    const startApiCall = useCallback(async () => {
        if (!payload) return;
        try {
            const created = await surveysAPI.guided.instantiate(payload);
            setCreatedSurvey(created);
            surveyIdRef.current = created.id;
            apiResolvedRef.current = true;
            // Bump the target into the translation range so the user sees
            // motion the instant the API resolves. From here, the polling
            // loop owns the target.
            if (targetPctRef.current < STEPS[2].ceiling) {
                targetPctRef.current = STEPS[2].ceiling;
            }
        } catch (err) {
            apiResolvedRef.current = true;
            const msg = err?.response?.data?.detail || t('surveys.guided.createFailed', 'Could not create the guided survey.');
            setErrorMsg(msg);
        }
    }, [payload, t]);

    useEffect(() => {
        if (!payload) return;
        if (startedRef.current) return;
        startedRef.current = true;
        startApiCall();
    }, [payload, startApiCall]);

    // ── The honest poll loop ─────────────────────────────────────
    useEffect(() => {
        if (!payload || errorMsg) return;
        let cancelled = false;
        let timer = null;

        const pollOnce = async () => {
            if (cancelled) return;
            if (!surveyIdRef.current) {
                timer = setTimeout(pollOnce, POLL_INTERVAL_MS);
                return;
            }
            try {
                const s = await surveysAPI.guided.status(surveyIdRef.current);
                if (cancelled) return;
                setStage(s.stage || 'queued');

                // Map backend progress into our visible bar.
                const bp = Number.isFinite(s.progress_pct) ? s.progress_pct : 0;
                const mapped = mapTranslationPct(bp);
                if (mapped > targetPctRef.current) {
                    targetPctRef.current = mapped;
                }

                if (s.ready) {
                    // The survey is genuinely usable. If translation failed
                    // we surface the warning but still let the user proceed
                    // — the survey exists in the source language.
                    if (s.status === 'failed' || s.stage === 'failed') {
                        setTranslationWarning(s.translation_error || t('surveys.guided.progress.partialTranslation', 'Some translations could not be generated, but your survey is ready to review.'));
                    }
                    // Crest to 100% smoothly.
                    targetPctRef.current = 100;
                    readyRef.current = true;
                    // Hold the "ready" state until the bar visually catches
                    // up — see the catch-up effect below.
                } else {
                    timer = setTimeout(pollOnce, POLL_INTERVAL_MS);
                }
            } catch (err) {
                // Soft-fail: try again. The polling loop is resilient to
                // transient errors (proxy hiccups, brief network blips).
                if (cancelled) return;
                timer = setTimeout(pollOnce, POLL_INTERVAL_MS);
            }
        };

        // Start the first poll once we have a survey_id (post API).
        // We watch surveyIdRef via a short retry tick.
        timer = setTimeout(pollOnce, 250);
        return () => { cancelled = true; if (timer) clearTimeout(timer); };
    }, [payload, errorMsg, t]);

    // ── Catch-up: reveal Done only when bar reaches 100% ─────────
    useEffect(() => {
        if (!readyRef.current) return;
        if (progress >= 99.5 && !ready) {
            // Snap to a clean 100 and reveal the completion card.
            setProgress(100);
            setReady(true);
        }
    }, [progress, ready]);

    // ── Handlers ─────────────────────────────────────────────────
    const onReview = () => {
        if (createdSurvey?.id) navigate(`/surveys/${createdSurvey.id}`);
    };
    const onBackToSurveys = () => navigate('/surveys');
    const onRetry = () => {
        // Reset everything for a fresh attempt with the same payload.
        setErrorMsg(null);
        setProgress(0);
        setStepIdx(0);
        setStage('queued');
        setElapsedMs(0);
        setCreatedSurvey(null);
        setReady(false);
        setTranslationWarning(null);
        targetPctRef.current = 0;
        apiResolvedRef.current = false;
        readyRef.current = false;
        surveyIdRef.current = null;
        startedRef.current = false;
        setTimeout(() => {
            startedRef.current = true;
            startApiCall();
        }, 50);
    };

    // ── Render helpers ───────────────────────────────────────────
    const elapsedSec = Math.floor(elapsedMs / 1000);
    const headerIcon = outcomeMeta?.icon || '✨';
    const hintKey = stageHintKey(stage, elapsedMs);
    const hintText = hintKey ? t(`surveys.guided.progress.hints.${hintKey}`, {
        translating: "Translating your survey into English, French and Spanish…",
        translatingLonger: "Translating your survey into English, French and Spanish… This can take a little longer for larger surveys.",
        queueLonger: "Getting your survey set up… Almost ready to start translating.",
        translationFailed: "Translation could not be generated, but your survey is still ready to review.",
    }[hintKey]) : null;

    if (!payload) return null;  // brief flash before redirect

    return (
        <div
            className="min-h-[calc(100vh-4rem)] flex items-start sm:items-center justify-center px-4 py-10 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900"
            data-testid="guided-progress-page"
        >
            <div className="w-full max-w-xl">
                {/* ─── Error ─── */}
                {errorMsg && (
                    <div
                        className="bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-900 rounded-xl shadow-lg p-6 space-y-4"
                        data-testid="guided-progress-error"
                        role="alert"
                    >
                        <div className="flex items-center gap-2 text-rose-600 dark:text-rose-300">
                            <AlertTriangle className="w-5 h-5" />
                            <h2 className="text-lg font-semibold">
                                {t('surveys.guided.progress.errorTitle', 'Something went wrong')}
                            </h2>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-200">{errorMsg}</p>
                        <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                            <Button type="button" variant="outline" size="sm" onClick={onBackToSurveys} data-testid="guided-progress-error-back">
                                <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                                {t('surveys.guided.progress.backToSurveys', 'Back to Surveys')}
                            </Button>
                            <Button type="button" size="sm" onClick={onRetry} data-testid="guided-progress-error-retry">
                                <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                {t('surveys.guided.progress.retry', 'Try again')}
                            </Button>
                        </div>
                    </div>
                )}

                {/* ─── Building ─── */}
                {!errorMsg && !ready && (
                    <div
                        className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg p-6 sm:p-8 space-y-6"
                        data-testid="guided-progress-building"
                    >
                        <header className="space-y-2 text-center sm:text-left">
                            <div className="flex items-center justify-center sm:justify-start gap-2 text-fuchsia-600 dark:text-fuchsia-300 text-xs font-semibold uppercase tracking-wide">
                                <Sparkles className="w-3.5 h-3.5" />
                                <span>{t('surveys.guided.progress.eyebrow', 'Guided Survey')}</span>
                            </div>
                            <h1
                                className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100 flex items-center justify-center sm:justify-start gap-2"
                                data-testid="guided-progress-title"
                            >
                                <span aria-hidden="true">{headerIcon}</span>
                                <span>{t('surveys.guided.progress.title', 'Building your survey…')}</span>
                            </h1>
                            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                                {t(
                                    'surveys.guided.progress.description',
                                    "We're creating your survey and preparing everything for you. Please don't close this page. Building a complete survey may take up to a minute depending on complexity.",
                                )}
                            </p>
                        </header>

                        <div className="space-y-2">
                            <Progress
                                value={Math.round(progress)}
                                className="h-2 bg-slate-200 dark:bg-slate-800"
                                data-testid="guided-progress-bar"
                            />
                            <div className="flex items-center justify-between text-xs text-slate-500">
                                <span className="inline-flex items-center gap-1" data-testid="guided-progress-elapsed">
                                    <Clock className="w-3 h-3" />
                                    {t('surveys.guided.progress.elapsed', 'Elapsed time: {{seconds}}s', { seconds: elapsedSec })}
                                </span>
                                <span className="tabular-nums" data-testid="guided-progress-percent">
                                    {Math.round(progress)}%
                                </span>
                            </div>
                        </div>

                        {/* Contextual hint — only when meaningful */}
                        {hintText && (
                            <div
                                className="rounded-md border border-fuchsia-200 dark:border-fuchsia-800 bg-fuchsia-50/60 dark:bg-fuchsia-950/30 px-3 py-2 text-xs text-fuchsia-900 dark:text-fuchsia-100"
                                data-testid="guided-progress-hint"
                            >
                                {hintText}
                            </div>
                        )}

                        <ul className="space-y-2.5" data-testid="guided-progress-steps">
                            {STEPS.map((s, i) => {
                                const state =
                                    i < stepIdx ? 'done'
                                        : (i === stepIdx ? 'active' : 'pending');
                                return (
                                    <li
                                        key={s.id}
                                        className="flex items-center gap-3 text-sm"
                                        data-testid={`guided-progress-step-${s.id}`}
                                        data-state={state}
                                    >
                                        <span
                                            className={[
                                                'flex-none w-5 h-5 rounded-full grid place-items-center',
                                                state === 'done'
                                                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                                                    : state === 'active'
                                                        ? 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-950/60 dark:text-fuchsia-300'
                                                        : 'bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500',
                                            ].join(' ')}
                                            aria-hidden="true"
                                        >
                                            {state === 'done' && <Check className="w-3 h-3" />}
                                            {state === 'active' && <Loader2 className="w-3 h-3 animate-spin" />}
                                            {state === 'pending' && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
                                        </span>
                                        <span
                                            className={[
                                                'flex-1',
                                                state === 'done' ? 'text-slate-500 dark:text-slate-400 line-through decoration-1' : '',
                                                state === 'active' ? 'text-slate-900 dark:text-slate-100 font-medium' : '',
                                                state === 'pending' ? 'text-slate-500 dark:text-slate-400' : '',
                                            ].join(' ')}
                                        >
                                            {t(`surveys.guided.progress.steps.${s.key}`, s.defaultLabel)}
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}

                {/* ─── Done ─── */}
                {!errorMsg && ready && (
                    <div
                        className="bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-900 rounded-xl shadow-lg p-6 sm:p-8 space-y-5"
                        data-testid="guided-progress-done"
                    >
                        <div className="flex items-center justify-center sm:justify-start gap-2">
                            <span
                                className="grid place-items-center w-9 h-9 rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/60 dark:text-emerald-300"
                                aria-hidden="true"
                            >
                                <Check className="w-5 h-5" />
                            </span>
                            <h1
                                className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-slate-100"
                                data-testid="guided-progress-done-title"
                            >
                                {t('surveys.guided.progress.doneTitle', 'Your survey is ready!')}
                            </h1>
                        </div>
                        <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                            {t(
                                'surveys.guided.progress.doneDescription',
                                'Your survey has been successfully created and is ready for review before publishing.',
                            )}
                        </p>

                        {translationWarning && (
                            <div
                                className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-900 dark:text-amber-100"
                                data-testid="guided-progress-translation-warning"
                            >
                                <span className="inline-flex items-center gap-1">
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    {translationWarning}
                                </span>
                            </div>
                        )}

                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 pt-1">
                            <Button
                                type="button"
                                onClick={onReview}
                                className="sm:flex-1"
                                data-testid="guided-progress-review-btn"
                            >
                                <Sparkles className="w-3.5 h-3.5 mr-1" />
                                {t('surveys.guided.progress.reviewBtn', 'Review Survey')}
                                <ArrowRight className="w-3.5 h-3.5 ml-1" />
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={onBackToSurveys}
                                className="sm:flex-1"
                                data-testid="guided-progress-back-btn"
                            >
                                <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                                {t('surveys.guided.progress.backToSurveys', 'Back to Surveys')}
                            </Button>
                        </div>

                        <div
                            className="rounded-md bg-fuchsia-50/60 dark:bg-fuchsia-950/30 border border-fuchsia-200 dark:border-fuchsia-800 p-3 text-sm"
                            data-testid="guided-progress-next-step"
                        >
                            <div className="font-semibold text-fuchsia-900 dark:text-fuchsia-100 mb-1">
                                {t('surveys.guided.progress.nextStepTitle', 'Next step')}
                            </div>
                            <p className="text-xs text-fuchsia-800 dark:text-fuchsia-200 leading-relaxed">
                                {t(
                                    'surveys.guided.progress.nextStepBody',
                                    "Review your questions, make any changes you'd like, then publish your survey when you're ready.",
                                )}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GuidedSurveyProgress;
